define('presentation/receptionist/viewModels/receptionistMessageViewModel',[
    'businessServices/contacts/contactsStateSingleton',
    'businessServices/events/eventManager',
    'common/collections/collectionFilter',
    'common/converters/phoneNumberFormatter',
    'common/promises/promiseFactory',
    'common/storage/commonState',
    'common/time/datetimeFormatter',
    'constants/liveReceptionistScriptTypeConstants',
    'constants/receptionistMessageConstants',
    'constants/scrollEventSources',
    'constants/systemObjectIconNameConstants',
    'presentation/receptionist/dataSources/messageSubscriptionDataSource',
    'presentation/receptionist/facades/receptionistMessageFacade'
], function () {
    return function () {
        const self = this;

        const DateTimeFormatterConstructor = require('common/time/datetimeFormatter');
        const _dateTimeFormatter = new DateTimeFormatterConstructor();

        const PhoneNumberFormatterConstructor = require('common/converters/phoneNumberFormatter');
        const _phoneNumberFormatter = new PhoneNumberFormatterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const _liveReceptionistScriptTypeConstants = require('constants/liveReceptionistScriptTypeConstants');
        const _receptionistMessageConstants = require('constants/receptionistMessageConstants');
        const _commonState = require('common/storage/commonState');
        const _contactsState = require('businessServices/contacts/contactsStateSingleton');
        const _systemObjectIconNames = require('constants/systemObjectIconNameConstants');

        let _facade = null;

        let isScreenAlreadyInitialized = false;

        self.receptionistMessage = ko.observable(null);
        self.receptionistMessageId = '';

        self.status = ko.observable('');
        self.isStatusChanging = ko.observable(false);

        self.junkStatus = ko.observable('');
        self.isJunk = ko.observable(false);

        self.originatorAvatar = ko.observable(null);
        self.originatorIconName = ko.observable('');
        self.originatorInitials = ko.observable('');
        self.originatorType = ko.observable('');
        self.originatorTitle = ko.observable('');
        self.originatorMetadata = ko.observable('');
        self.callHistoryId = ko.observable('');

        self.hostedNumberId = ko.observable('');
        self.hostedPhoneNumber = ko.observable(null);
        self.hostedPhoneNumberFormatted = ko.observable(null);

        self.liveReceptionistScriptId = ko.observable('');
        self.liveReceptionistScriptName = ko.observable(null);
        self.liveReceptionistScriptMetadata = ko.observable(null);
        self.liveReceptionistScriptTypeIconName = ko.observable(null);

        self.formResponses = ko.observableArray([]);
        self.recipients  = ko.observableArray([]);

        self.callDetails = ko.observable('');

        self.messageLeftDateTime = ko.observable(null);
        self.date = ko.observable(null);

        self.isStatusNew = ko.pureComputed(() => {
            return (self.status() === _receptionistMessageConstants.status.new.name);
        });

        self.statusI18nKey = ko.pureComputed(() => {
            return {key: 'isNew', options: {context: self.status().toLowerCase()}};
        });

        self.showNotFoundMessage = ko.pureComputed(() => {
            return self.receptionistMessage() === null;
        });

        self.toggleReceptionistMessageStatus = () => {
            return _promiseFactory.deferIndefinitely((promise) => {
                const currentStatus = self.status();
                const newStatus = currentStatus === _receptionistMessageConstants.status.new.name ?
                    _receptionistMessageConstants.status.reviewed.name :
                    _receptionistMessageConstants.status.new.name;

                if (currentStatus === newStatus) {
                    self.isStatusChanging(false);
                    promise.resolve();
                    return;
                }

                self.isStatusChanging(true);

                _facade.setReceptionistMessageStatus(self.receptionistMessageId, newStatus)
                    .fail(promise.reject)
                    .done((isSuccess) => {
                        if (isSuccess){
                            self.status(newStatus);
                        }
                        self.isStatusChanging(false);
                        promise.resolve();
                    });
            });
        };

        self.detached = () => {
            isScreenAlreadyInitialized = false;
        };

        self.activate = (messageId) => {
            const FacadeConstructor = require('presentation/receptionist/facades/receptionistMessageFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            return _initialize(messageId);
        };

        const _initialize = (messageId) => {
            if (isScreenAlreadyInitialized) {
                return _promiseFactory.wait();
            }
            isScreenAlreadyInitialized = true;

            _facade.getReceptionistMessageById(messageId)
                .done((receptionistMessage) => {
                    if (receptionistMessage){
                        self.receptionistMessage(receptionistMessage);
                        _setReceptionistMessageData(receptionistMessage);
                    } else {
                        self.receptionistMessage(null);
                    }
                });
            return _promiseFactory.wait();
        };

        const formatPhoneNumber = (phoneNumber) => {
            return _phoneNumberFormatter.formatInternationalNumber(phoneNumber);
        };

        const _setReceptionistMessageData = (data) => {
            const {
                receptionistMessageId,
                status,
                isJunk,
                junkStatus,
                callHistoryId,
                callerAni,
                callerLocationState,
                callerLocationCity,
                accountHostedNumberId,
                liveReceptionistScriptId,
                liveReceptionistScriptDisplayName,
                liveReceptionistScriptType,
                callDetails,
                formResponses,
                recipients,
                createdDateTime,
                interactionType,
                participantName,
                participantEmailAddress,
                widgetDisplayName
            } = data;

            self.receptionistMessageId = receptionistMessageId;

            switch (status) {
                case _receptionistMessageConstants.status.new.id:
                    self.status(_receptionistMessageConstants.status.new.name);
                    break;
                case _receptionistMessageConstants.status.reviewed.id:
                    self.status(_receptionistMessageConstants.status.reviewed.name);
                    break;
            }

            self.isJunk = ko.observable(isJunk);
            switch (junkStatus) {
                case _receptionistMessageConstants.junkStatus.abandoned.id:
                    self.junkStatus(_receptionistMessageConstants.junkStatus.abandoned.name);
                    break;
                case _receptionistMessageConstants.junkStatus.hangup.id:
                    self.junkStatus(_receptionistMessageConstants.junkStatus.hangup.name);
                    break;
                case _receptionistMessageConstants.junkStatus.spam.id:
                    self.junkStatus(_receptionistMessageConstants.junkStatus.spam.name);
                    break;
                case _receptionistMessageConstants.junkStatus.externalWebsite.id:
                    self.junkStatus(_receptionistMessageConstants.junkStatus.externalWebsite.name);
                    break;
            }

            switch (liveReceptionistScriptType) {
                case _liveReceptionistScriptTypeConstants.LiveReceptionist:
                    self.liveReceptionistScriptTypeIconName(_systemObjectIconNames.hostedNumber);
                    break;
                case _liveReceptionistScriptTypeConstants.WebChat:
                    self.liveReceptionistScriptTypeIconName(_systemObjectIconNames.webChatWidget);
                    break;
                case _liveReceptionistScriptTypeConstants.OutboundCalling:
                    self.liveReceptionistScriptTypeIconName(_systemObjectIconNames.outboundCallingCampaign);
                    break;
            }

            let originatorAvatar = null;
            let originatorIconName = null;
            let originatorInitials = null;
            let originatorType = 'external';
            let originatorTitle;
            let originatorMetadata = null;

            let liveReceptionistScriptMetadata = null;

            let hostedPhoneNumberFormatted = null;
            let hostedPhoneNumberRaw = null;

            switch (interactionType) {
                case _receptionistMessageConstants.interactionType.chat.id:
                    originatorIconName = _systemObjectIconNames.webChatWidget;
                    originatorTitle = participantName;
                    originatorMetadata = participantEmailAddress;
                    liveReceptionistScriptMetadata = widgetDisplayName;
                    break;
                default:
                    originatorIconName = _systemObjectIconNames.hostedNumber;
                    const contact = _contactsState.getContactByPhoneNumber(callerAni);
                    if (contact) {
                        originatorAvatar = contact.avatarUrl();
                        originatorTitle = contact.displayName();
                        originatorInitials = contact.initials();
                        originatorType = _commonState.types.contact;
                        const matchingNumber = contact.phoneNumbers().find(x => _phoneNumberFormatter.toEOneSixFour(x.rawPhoneNumber()) === _phoneNumberFormatter.toEOneSixFour(callerAni));
                        originatorMetadata = matchingNumber ? matchingNumber.label() : null;
                    } else if (callerAni) {
                        originatorTitle = formatPhoneNumber(callerAni);
                        originatorMetadata = _phoneNumberFormatter.buildLocation(callerAni, callerLocationCity, callerLocationState);
                    }

                    let hostedNumber = _commonState.hostedNumbers().find((hostedNumber) => {
                        return hostedNumber.id === accountHostedNumberId;
                    });

                    if (hostedNumber) {
                        hostedPhoneNumberFormatted = hostedNumber.phoneNumber();
                        hostedPhoneNumberRaw = _phoneNumberFormatter.clean(hostedNumber.phoneNumber());
                        liveReceptionistScriptMetadata = hostedNumber.name();
                    }
            }

            if (isJunk) {
                originatorTitle = self.junkStatus();
                originatorIconName = 'actionDelete';
            }

            self.originatorAvatar(originatorAvatar);
            self.originatorIconName(originatorIconName);
            self.originatorInitials(originatorInitials);
            self.originatorType(originatorType);
            self.originatorTitle(originatorTitle);
            self.originatorMetadata(originatorMetadata);
            self.callHistoryId(callHistoryId);

            self.hostedNumberId(accountHostedNumberId);
            self.hostedPhoneNumber(hostedPhoneNumberRaw);
            self.hostedPhoneNumberFormatted(hostedPhoneNumberFormatted);

            self.liveReceptionistScriptId(liveReceptionistScriptId);
            self.liveReceptionistScriptName(liveReceptionistScriptDisplayName);
            self.liveReceptionistScriptMetadata(liveReceptionistScriptMetadata);

            self.formResponses(formResponses);
            self.recipients(recipients);

            self.messageLeftDateTime(createdDateTime);
            self.date(_dateTimeFormatter.humanizedDay(createdDateTime));

            self.callDetails(callDetails);
        };
    };
});
